import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Sparkles, MapPin, Wrench, Building2, ShieldCheck, AlertTriangle, ExternalLink, CheckCircle2, Loader2, Radar, Umbrella, Landmark } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_STYLES = {
  license:    { icon: ShieldCheck, accent: "text-dox-orange",   ring: "border-dox-orange/30"   },
  compliance: { icon: Radar,       accent: "text-dox-magenta",  ring: "border-dox-magenta/30"  },
  insurance:  { icon: Umbrella,    accent: "text-dox-lightBlue",ring: "border-dox-lightBlue/30"},
  bond:       { icon: Landmark,    accent: "text-dox-lightBlue",ring: "border-dox-lightBlue/30"},
};

/**
 * The heart of the State Compliance Intelligence Engine — proactive
 * recommendations tailored to the active business.
 */
export default function ComplianceRoadmap() {
  const [states, setStates] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradeCategories, setTradeCategories] = useState([]);
  const [tradeSearch, setTradeSearch] = useState("");
  const [data, setData] = useState(null);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [employeeCount, setEmployeeCount] = useState("");
  const [usdot, setUsdot] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: t }, { data: recs }] = await Promise.all([
        api.get("/compliance/states"),
        api.get("/compliance/trades"),
        api.get("/compliance/recommendations"),
      ]);
      setStates(s.items || []);
      setTrades(t.items || []);
      setTradeCategories(t.categories || []);
      setData(recs);
      setSelectedStates(recs.business?.states || []);
      setSelectedTrades(recs.business?.trades || []);
      setEmployeeCount(recs.business?.employee_count || "");
      setUsdot(recs.business?.usdot_number || "");
    } catch { toast.error("Couldn't load compliance data."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/compliance/business/profile", {
        states: selectedStates,
        trades: selectedTrades,
        employee_count: employeeCount ? parseInt(employeeCount, 10) : null,
        usdot_number: usdot || null,
      });
      toast.success("Profile updated — Dox is recomputing your roadmap.");
      await load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const toggleState = (abbrev) => setSelectedStates((prev) => prev.includes(abbrev) ? prev.filter((s) => s !== abbrev) : [...prev, abbrev]);
  const toggleTrade = (slug) => setSelectedTrades((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);

  const licenses = useMemo(() => (data?.recommendations || []).filter((r) => r.kind === "license"), [data]);
  const insuranceBonds = useMemo(() => (data?.recommendations || []).filter((r) => r.kind === "insurance" || r.kind === "bond"), [data]);
  const compliance = useMemo(() => (data?.recommendations || []).filter((r) => r.kind === "compliance"), [data]);

  if (!data) return <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="space-y-8" data-testid="compliance-roadmap-view">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-dox-orange" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-magenta">State Compliance Intelligence</div>
        </div>
        <h2 className="font-display font-black text-3xl">Point me at your business. I&apos;ll handle the rest.</h2>
        <p className="text-white/60 mt-3 max-w-2xl">
          Tell me which states you operate in and which trades you run. I&apos;ll pull the right licenses,
          agencies, fees, and renewal cadence — and offer you a one-tap YES on every single one so you never
          have to log into another government portal.
        </p>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Documents on file" value={data.counts.documents_on_file} />
        <Stat label="Open tasks" value={data.counts.open_tasks} />
        <Stat label="In-flight filings" value={data.counts.in_flight_submissions} />
        <Stat label="Licenses to hold" value={data.counts.recommended_licenses} />
      </div>

      {/* Profile picker */}
      <div className="glass-card p-6" data-testid="roadmap-profile-card">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-dox-orange" /> States of operation
            </label>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-2">
              {states.map((s) => {
                const on = selectedStates.includes(s.abbrev);
                return (
                  <button
                    key={s.abbrev}
                    data-testid={`state-chip-${s.abbrev}`}
                    onClick={() => toggleState(s.abbrev)}
                    className={`rounded-full text-xs px-2.5 py-1 border transition-colors ${on ? "bg-dox-orange text-white border-dox-orange" : "glass border-white/10 text-white/70 hover:border-white/25"}`}
                    title={s.name}
                  >
                    {s.abbrev}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-white/80 flex items-center gap-2 mb-3">
              <Wrench size={14} className="text-dox-magenta" /> Trades you operate in
            </label>
            <input
              type="search"
              value={tradeSearch}
              onChange={(e) => setTradeSearch(e.target.value)}
              placeholder={`Search ${trades.length}+ trades…`}
              data-testid="roadmap-trades-search"
              aria-label="Search trades"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2 text-white placeholder:text-white/40 outline-none focus:border-dox-magenta focus:ring-2 focus:ring-dox-magenta/20 transition text-sm mb-3"
            />
            <div className="flex flex-wrap gap-1.5 max-h-72 overflow-y-auto pr-2 scrollbar-thin">
              {(() => {
                const q = tradeSearch.trim().toLowerCase();
                const cats = tradeCategories.length ? tradeCategories : [{ slug: "all", label: "Trades", items: trades }];
                const nodes = [];
                cats.forEach((cat) => {
                  const items = (cat.items || []).filter(
                    (t) => !q || t.name.toLowerCase().includes(q) || t.slug.includes(q)
                  );
                  if (!items.length) return;
                  if (cats.length > 1) {
                    nodes.push(
                      <div key={`h-${cat.slug}`} className="w-full text-[10px] uppercase tracking-widest text-white/40 font-bold mt-2 mb-1">
                        {cat.label}
                      </div>
                    );
                  }
                  items.forEach((t) => {
                    const on = selectedTrades.includes(t.slug);
                    nodes.push(
                      <button
                        key={t.slug}
                        data-testid={`trade-chip-${t.slug}`}
                        onClick={() => toggleTrade(t.slug)}
                        className={`rounded-full text-xs px-2.5 py-1 border transition-colors ${on ? "bg-dox-magenta text-white border-dox-magenta" : "glass border-white/10 text-white/70 hover:border-white/25"}`}
                      >
                        {t.name}
                      </button>
                    );
                  });
                });
                if (nodes.length === 0) {
                  nodes.push(
                    <div key="none" className="w-full text-xs text-white/45 py-3">
                      No match for &ldquo;{tradeSearch}&rdquo;. Dox covers every licensed trade in America — try another keyword.
                    </div>
                  );
                }
                return nodes;
              })()}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <NumberInput label="Employees" value={employeeCount} onChange={setEmployeeCount} placeholder="e.g. 12" testId="roadmap-employees" />
          <TextInput label="USDOT # (if any)" value={usdot} onChange={setUsdot} placeholder="e.g. 3210987" testId="roadmap-usdot" />
        </div>
        <div className="flex items-center justify-end mt-6">
          <button
            data-testid="roadmap-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-dox-orange px-6 py-2.5 min-h-[44px] text-sm font-bold text-white hover:bg-dox-orangeHover transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {saving ? "Recomputing…" : "Update roadmap"}
          </button>
        </div>
      </div>

      {/* Licenses */}
      {licenses.length > 0 && (
        <Section title="Licenses to hold" icon={ShieldCheck}>
          <div className="grid md:grid-cols-2 gap-4">
            {licenses.map((r, i) => <RecCard key={`l${i}`} rec={r} />)}
          </div>
        </Section>
      )}

      {/* Insurance & bonds — every license drags these obligations along */}
      {insuranceBonds.length > 0 && (
        <Section title="Insurance & bonds" icon={Umbrella}>
          <div className="grid md:grid-cols-2 gap-4">
            {insuranceBonds.map((r, i) => <RecCard key={`ib${i}`} rec={r} />)}
          </div>
        </Section>
      )}

      {/* Cross-cutting compliance */}
      {compliance.length > 0 && (
        <Section title="Cross-cutting compliance" icon={Radar}>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {compliance.map((r, i) => <RecCard key={`c${i}`} rec={r} />)}
          </div>
        </Section>
      )}

      {/* Empty state */}
      {licenses.length === 0 && compliance.length === 0 && insuranceBonds.length === 0 && (
        <div className="glass-card p-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/5 border border-white/10 mb-3">
            <Building2 size={22} className="text-white/50" />
          </div>
          <div className="text-white/80 font-semibold">Tell Dox where you work.</div>
          <div className="text-white/45 text-sm mt-1 max-w-md mx-auto">Select at least one state + one trade above and hit &ldquo;Update roadmap&rdquo; to see Dox&apos;s recommendations for your business.</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass-card p-4">
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">{label}</div>
      <div className="font-display font-black text-3xl text-white">{value ?? 0}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-dox-orange" />
        <h3 className="font-display font-bold text-xl text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function RecCard({ rec }) {
  const style = KIND_STYLES[rec.kind] || KIND_STYLES.compliance;
  const Icon = style.icon;
  const [state, setState] = useState("idle"); // idle | working | done | error

  const doYes = async () => {
    setState("working");
    try {
      const { data: h } = await api.post("/compliance/handoff", { recommendation: rec });
      setState("done");
      const undoTaskId = h?.task_id;
      toast.success("Dox is on it. I'll notify you when it's filed.", {
        duration: 5500,
        action: undoTaskId ? {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/compliance/handoff/undo", { task_id: undoTaskId });
              setState("idle");
              toast.message("Handoff canceled. I'm not filing anything.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't undo — it may have already been filed.");
            }
          },
        } : undefined,
      });
      try {
        const { data: cov } = await api.get("/calendar/coverage");
        if (cov?.synced > 0) {
          toast.message(
            `Calendar coverage: ${cov.synced} of ${cov.total} ${cov.percent === 100 ? "✓" : `· ${cov.percent}%`}`,
            { duration: 3500 }
          );
        }
      } catch { /* non-blocking */ }
    } catch (e) {
      setState("error");
      toast.error(e?.response?.data?.detail || "Dox couldn't queue this — try again.");
    }
  };

  return (
    <div className={`glass-card p-5 ${style.ring} flex flex-col gap-4`} data-testid={`rec-${rec.slug || rec.title}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
            <Icon size={18} className={style.accent} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-white leading-tight">{rec.title}</div>
            <div className="text-[11px] text-white/50 mt-0.5">{rec.agency || rec.authority}</div>
          </div>
        </div>
        {rec.priority === "high" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-dox-orange/20 border border-dox-orange/40 px-2 py-0.5 text-[10px] font-bold text-dox-orange">
            <AlertTriangle size={10} /> HIGH
          </span>
        )}
      </div>
      {rec.notes && <p className="text-xs text-white/55 leading-relaxed">{rec.notes}</p>}
      <div className="flex flex-wrap gap-1.5">
        {rec.renewal_cycle_months ? (
          <Chip>Renews every {rec.renewal_cycle_months} mo</Chip>
        ) : null}
        {rec.typical_renewal_fee_usd ? (
          <Chip>${rec.typical_renewal_fee_usd} fee</Chip>
        ) : null}
        {rec.typical_bond_usd ? (
          <Chip>${rec.typical_bond_usd.toLocaleString()} bond</Chip>
        ) : null}
        {rec.exam_required && <Chip>Exam</Chip>}
        {rec.insurance_required && <Chip>Insurance</Chip>}
        {rec.continuing_education_hours_per_cycle ? (
          <Chip>{rec.continuing_education_hours_per_cycle}h CE</Chip>
        ) : null}
      </div>

      {/* Dox conversational YES CTA */}
      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
        {state === "idle" && (
          <>
            <div className="text-sm text-white/90">Want me to handle this for you?</div>
            <button
              data-testid={`rec-yes-${rec.slug || rec.title}`}
              onClick={doYes}
              className="mt-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-black px-5 py-2 min-h-[44px] text-sm shadow-lg shadow-emerald-500/25 transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center gap-2"
            >
              <Sparkles size={13} /> YES — Do it for me
            </button>
          </>
        )}
        {state === "working" && (
          <div className="flex items-center gap-2 text-sm text-white/85">
            <Loader2 size={14} className="animate-spin text-emerald-300" />
            Gathering documents · preparing packet · queuing filing…
          </div>
        )}
        {state === "done" && (
          <div className="flex items-start gap-2 text-sm text-white/90">
            <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-emerald-300">I&apos;m on it.</div>
              <div className="text-xs text-white/65 mt-0.5">Scheduled, packet prepared, filing queued. Check the Command Center for status.</div>
            </div>
          </div>
        )}
        {state === "error" && (
          <div className="flex items-center gap-2 text-sm text-rose-300">
            <AlertTriangle size={14} /> Couldn&apos;t queue it. <button onClick={doYes} className="underline">Try again</button>
          </div>
        )}
      </div>

      {(rec.official_link || rec.agency_url) && (
        <a
          href={rec.official_link || rec.agency_url}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-semibold text-white/50 hover:text-white/85 inline-flex items-center gap-1"
        >
          View agency source <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function Chip({ children }) {
  return <span className="text-[10px] rounded-full glass border border-white/10 px-2 py-0.5 text-white/65">{children}</span>;
}

function NumberInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-xs tracking-widest uppercase text-white/40 mb-1.5">{label}</div>
      <input
        data-testid={testId}
        type="number" min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-dox-orange transition"
      />
    </label>
  );
}
function TextInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-xs tracking-widest uppercase text-white/40 mb-1.5">{label}</div>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-dox-orange transition"
      />
    </label>
  );
}
